import { Component, type ErrorInfo, type ReactNode } from 'react';
import { EmptyState } from '../ui';

/**
 * A render that throws must not leave a white page. The message is shown rather than
 * hidden: on a builder, the failure is usually something the person can act on or
 * report, and a generic apology tells them nothing.
 */
export default class ErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  state: { error: Error | null } = { error: null };

  static getDerivedStateFromError(error: Error): { error: Error } {
    return { error };
  }

  /*
   * Not sent to analytics. src/analytics/events.ts is a deliberately PII-free catalog —
   * "no free text, no record content", enforced by a whitelist and a test — and an error
   * message is free text that can quote a person's own data. The console keeps it local
   * to whoever is looking, and the screen shows it to the person who hit it.
   */
  componentDidCatch(error: Error, info: ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error('[saddle] render failed', error, info.componentStack);
  }

  render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;
    return (
      <EmptyState
        icon="close"
        title="Something went wrong"
        body={error.message}
        ctaTitle="Reload"
        onCta={() => window.location.reload()}
      />
    );
  }
}
